import { useSyncExternalStore } from 'react';
import i18n from './config';

/**
 * Re-renders the calling component (and so, through fresh child elements,
 * its whole subtree) whenever i18next's active language changes — WITHOUT
 * unmounting anything.
 *
 * Why this exists: the ~800 `t()` call sites are plain function calls, not
 * `useTranslation()` hooks, so nothing subscribes them to language changes.
 * LearnerLayout used to force a refresh by putting `key={language-generation}`
 * on the learner subtree — a full REMOUNT, which threw away every piece of
 * local state, so a learner who switched language half-way through the
 * sign-up / profile-setup form lost everything they had typed. A re-render
 * refreshes the same text and keeps component state.
 *
 * Must be called by the component that CREATES the route elements (App.tsx):
 * React skips children whose element identity is unchanged, so a re-render
 * lower in the tree (inside the layout) would never reach the pages.
 *
 * Returns the active language only so the call is not tree-shaken/ignored.
 */
function subscribe(onChange: () => void): () => void {
  i18n.on('languageChanged', onChange);
  return () => {
    i18n.off('languageChanged', onChange);
  };
}

function getSnapshot(): string {
  return i18n.language;
}

export function useRerenderOnLanguageChange(): string {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}
